type ClassValue = string | number | bigint | null | undefined | boolean | ClassValue[] | Record<string, boolean | null | undefined>;

/** Minimal className joiner (clsx-compatible subset). */
export function cn(...inputs: ClassValue[]): string {
  const out: string[] = [];
  const walk = (v: ClassValue) => {
    if (!v) return;
    if (v === true) return;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'bigint') out.push(String(v));
    else if (Array.isArray(v)) v.forEach(walk);
    else for (const [k, on] of Object.entries(v)) if (on) out.push(k);
  };
  inputs.forEach(walk);
  return out.join(' ');
}

export function formatNumber(n: number | null | undefined, opts: Intl.NumberFormatOptions = {}): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '0';
  return new Intl.NumberFormat('en-GB', opts).format(n);
}

export function formatCompact(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '0';
  return new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

export function formatPercent(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '0%';
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(digits)}%`;
}

export function formatDate(value?: string | Date | null, style: 'short' | 'medium' | 'long' = 'medium'): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  const map: Record<typeof style, Intl.DateTimeFormatOptions> = {
    short: { month: 'short', day: 'numeric' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' },
  };
  return new Intl.DateTimeFormat('en-US', map[style]).format(d);
}

export function relativeTime(value?: string | Date | null): string {
  if (!value) return 'Never';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diff = Date.now() - d.getTime();
  if (Number.isNaN(diff)) return '—';
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? '' : 's'} ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(d, 'short');
}

export function greeting(date = new Date()): string {
  const h = date.getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function initials(name?: string | null, email?: string | null): string {
  const src = (name && name.trim()) || (email ? email.split('@')[0] : '') || '?';
  const parts = src.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  const a = parts[0]?.[0] ?? '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : parts[0]?.[1] ?? '';
  return (a + b).toUpperCase();
}

export function firstName(name?: string | null): string {
  if (!name) return 'there';
  return name.trim().split(/\s+/)[0];
}

/** antd's toast is loaded on demand so this shared helper does not pull antd into the public bundle. */
const toast = () =>
  Promise.all([import('antd'), import('../theme/antdStatic')]).then(([antd, statics]) => {
    statics.configureAntdStatics();
    return antd.message;
  });

export async function copyToClipboard(text: string, label = 'Copied to clipboard'): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    (await toast()).success(label);
    return true;
  } catch {
    (await toast()).error('Could not copy');
    return false;
  }
}

export function truncateMiddle(value: string, max = 40): string {
  if (value.length <= max) return value;
  const half = Math.floor((max - 1) / 2);
  return `${value.slice(0, half)}…${value.slice(-half)}`;
}

export function stripProtocol(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export function maskKey(key: string, visible = 4): string {
  if (!key) return '';
  if (key.length <= visible * 2 + 4) return key;
  const prefixMatch = key.match(/^([a-z]+_[a-z]+_)/i);
  const prefix = prefixMatch ? prefixMatch[1] : '';
  const body = key.slice(prefix.length);
  return `${prefix}${body.slice(0, visible)}${'•'.repeat(12)}${body.slice(-visible)}`;
}

export function isRtl(text?: string | null): boolean {
  if (!text) return false;
  // Hebrew, Arabic, Syriac, Thaana, NKo, Arabic supplement/extended and presentation forms.
  return /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/.test(text);
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function toCsv(rows: Array<Record<string, unknown>>, columns: string[]): string {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [columns.join(','), ...rows.map((r) => columns.map((c) => esc(r[c])).join(','))].join('\n');
}

export function getErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  const e = error as { response?: { data?: { message?: string | string[] } }; message?: string };
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.join(', ');
  if (typeof m === 'string') return m;
  return e?.message || fallback;
}

export const SHORT_URL_BASE: string = (import.meta.env.VITE_SHORT_URL_BASE as string | undefined) || 'https://cmsh.io';
export const SHORT_HOST = stripProtocol(SHORT_URL_BASE);

export function shortUrlFor(code: string, domain?: string | null): string {
  const host = domain ? domain.replace(/^https?:\/\//, '') : SHORT_HOST;
  return `https://${host}/${code}`;
}
